import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Users, Plus, Search, Layers, Edit3, Trash2, BookOpen 
} from 'lucide-react';

const BatchManagement = () => {
  const [batches, setBatches] = useState([]);
  const [academicYears, setAcademicYears] = useState([]);
  const [formData, setFormData] = useState({
    batch_name: '',
    acd_id: '',
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });
  const [editingBatch, setEditingBatch] = useState(null);

  // Load data on mount
  useEffect(() => {
    fetchAcademicYears();
    fetchBatches();
  }, []);

  const fetchAcademicYears = async () => {
    try {
      const res = await axios.get('/api/academic-years');
      setAcademicYears(res.data);
    } catch (err) {
      console.error('Failed to load academic years', err);
      setMessage({ text: 'Failed to load academic years', type: 'error' });
    }
  };

  const fetchBatches = async () => {
    try {
      const res = await axios.get('/api/batches');
      setBatches(res.data);
    } catch (err) {
      console.error('Failed to load batches', err);
      setMessage({ text: 'Failed to load batches', type: 'error' });
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.batch_name.trim() || !formData.acd_id) {
      setMessage({ text: 'Please fill all required fields', type: 'error' });
      return;
    }

    setLoading(true);
    setMessage({ text: '', type: '' });

    try {
      if (editingBatch) {
        await axios.put(`/api/batches/${editingBatch.batch_id}`, formData);
        setMessage({ text: 'Batch updated successfully!', type: 'success' });
      } else {
        await axios.post('/api/batches', formData);
        setMessage({ text: 'Batch created successfully!', type: 'success' });
      }
      setFormData({ batch_name: '', acd_id: '' });
      setEditingBatch(null);
      fetchBatches();
    } catch (err) {
      console.error(err);
      setMessage({
        text: err.response?.data?.error || 'Failed to save batch',
        type: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (batch) => {
    setEditingBatch(batch);
    setFormData({
      batch_name: batch.batch_name,
      acd_id: batch.acd_id,          // corrected field name
    });
  };

  const handleDelete = async (batchId) => {
    // eslint-disable-next-line no-restricted-globals
    if (!window.confirm('Are you sure you want to delete this batch?')) return;
    
    try {
      await axios.delete(`/api/batches/${batchId}`);
      setMessage({ text: 'Batch deleted successfully!', type: 'success' });
      fetchBatches();
    } catch (err) {
      setMessage({ text: 'Failed to delete batch', type: 'error' });
    }
  };

  // Simple client-side filtering
  const filteredBatches = batches.filter((batch) => {
    const matchesSearch =
      batch.batch_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      batch.year_name.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  return (
    <div style={styles.container}>
      <div style={styles.contentWrapper}>
        {/* Header */}
        <header style={styles.header}>
          <div style={styles.headerLeft}>
            <div style={styles.iconBg}>
              <Users size={28} style={styles.headerIcon} />
            </div>
            <div>
              <h1 style={styles.title}>Batch Management</h1>
              <p style={styles.subtitle}>Manage academic batches and cohorts</p>
            </div>
          </div>
          <button 
            style={styles.newBtn} 
            onClick={() => { 
              setEditingBatch(null); 
              setFormData({ batch_name: '', acd_id: '' }); 
            }}
          >
            <Plus size={20} />
            {editingBatch ? 'Cancel Edit' : 'New Batch'}
          </button>
        </header>

        {/* Form Card */}
        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <h2 style={styles.cardTitle}>
              <Layers size={24} />
              {editingBatch ? 'Edit Batch' : 'Add New Batch'}
            </h2>
          </div>

          {message.text && (
            <div style={{
              ...styles.message,
              ...(message.type === 'success' ? styles.successMsg : styles.errorMsg)
            }}>
              {message.text}
            </div>
          )}

          <form onSubmit={handleSubmit} style={styles.form}>
            <div style={styles.formGrid}>
              <div style={styles.inputGroup}>
                <label style={styles.label}>Batch Name <span style={styles.required}>*</span></label>
                <input
                  type="text"
                  name="batch_name"
                  value={formData.batch_name}
                  onChange={handleInputChange}
                  placeholder="e.g. BCA 2023-2026"
                  style={styles.input}
                  required
                />
              </div>

              <div style={styles.inputGroup}>
                <label style={styles.label}>Academic Year <span style={styles.required}>*</span></label>
                <select
                  name="acd_id"
                  value={formData.acd_id}
                  onChange={handleInputChange}
                  style={styles.select}
                  required
                >
                  <option value="">Select Year</option>
                  {academicYears.map((year) => (
                    <option key={year.acd_id} value={year.acd_id}>
                      {year.year_name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div style={styles.formActions}>
              <button
                type="button"
                onClick={() => { 
                  setEditingBatch(null); 
                  setFormData({ batch_name: '', acd_id: '' }); 
                }}
                style={styles.cancelBtn}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                style={{
                  ...styles.submitBtn,
                  ...(loading && styles.submitBtnDisabled)
                }}
              >
                {loading ? (
                  <>
                    <div style={styles.spinner}></div>
                    Saving...
                  </>
                ) : editingBatch ? (
                  'Update Batch'
                ) : (
                  'Create Batch'
                )}
              </button>
            </div>
          </form>
        </div>

        {/* Batches Table */}
        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <h2 style={styles.cardTitle}>
              <BookOpen size={24} />
              Batches List ({filteredBatches.length})
            </h2>
            <div style={styles.searchContainer}>
              <Search size={20} style={styles.searchIcon} />
              <input
                type="text"
                placeholder="Search batches..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={styles.searchInput}
              />
            </div>
          </div>

          <div style={styles.tableContainer}>
            {filteredBatches.length === 0 ? (
              <div style={styles.emptyState}>
                <BookOpen size={64} style={{ color: '#9ca3af' }} />
                <h3 style={styles.emptyTitle}>No batches found</h3>
                <p style={styles.emptySubtitle}>Create your first batch to get started</p>
              </div>
            ) : (
              <div style={styles.tableWrapper}>
                <table style={styles.table}>
                  <thead>
                    <tr style={styles.tableHeadRow}>
                      <th style={styles.tableHead}>Batch Name</th>
                      <th style={styles.tableHead}>Academic Year</th>
                      <th style={styles.tableHead}>Batch ID</th>
                      <th style={styles.tableHead}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredBatches.map((batch) => (
                      <tr key={batch.batch_id} style={styles.tableRow}>
                        <td style={styles.tableCell}>
                          <div style={styles.cellContent}>
                            <div style={styles.batchName}>{batch.batch_name}</div>
                          </div>
                        </td>
                        <td style={styles.tableCell}>
                          <div style={styles.academicYearChip}>{batch.year_name}</div>
                        </td>
                        <td style={styles.tableCell}>
                          <code style={styles.batchId}>{batch.batch_id}</code>
                        </td>
                        <td style={styles.tableCell}>
                          <div style={styles.actionButtons}>
                            <button
                              onClick={() => handleEdit(batch)}
                              style={styles.editBtn}
                              title="Edit"
                            >
                              <Edit3 size={16} />
                            </button>
                            <button
                              onClick={() => handleDelete(batch.batch_id)}
                              style={styles.deleteBtn}
                              title="Delete"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const styles = {
  container: {
    maxWidth: '1400px',
    margin: '0 auto',
    padding: '32px',
    background: '#f8fafc',
    fontFamily: 'system-ui, -apple-system, sans-serif',
  },
  contentWrapper: {
    maxWidth: '1400px',
    margin: '0 auto'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '32px'
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px'
  },
  iconBg: {
    width: '56px',
    height: '56px',
    background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
    borderRadius: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  headerIcon: {
    color: 'white'
  },
  title: {
    fontSize: '28px',
    fontWeight: '700',
    color: '#1e293b',
    margin: 0
  },
  subtitle: {
    color: '#64748b',
    fontSize: '16px',
    margin: 0
  },
  newBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '12px 24px',
    background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
    color: '#fff',
    border: 'none',
    borderRadius: '12px',
    cursor: 'pointer',
    fontWeight: '600',
    fontSize: '15px',
  },
  card: {
    background: '#fff',
    borderRadius: '20px',
    boxShadow: '0 10px 25px rgba(0,0,0,0.08)',
    border: '1px solid #e2e8f0',
    overflow: 'hidden',
    marginBottom: '32px'
  },
  cardHeader: {
    padding: '28px 32px',
    borderBottom: '1px solid #f1f5f9'
  },
  cardTitle: {
    fontSize: '24px',
    fontWeight: '700',
    color: '#1e293b',
    margin: 0,
    display: 'flex',
    alignItems: 'center',
    gap: '12px'
  },
  message: {
    padding: '16px 24px',
    borderRadius: '12px',
    marginBottom: '24px',
    fontWeight: '500'
  },
  successMsg: {
    background: 'linear-gradient(90deg, #dcfce7, #bbf7d0)',
    color: '#166534',
    border: '1px solid #86efac'
  },
  errorMsg: {
    background: 'linear-gradient(90deg, #fee2e2, #fecaca)',
    color: '#dc2626',
    border: '1px solid #fca5a5'
  },
  form: {
    padding: '32px'
  },
  formGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '24px',
    marginBottom: '32px'
  },
  inputGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
  },
  label: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#374151'
  },
  required: {
    color: '#ef4444'
  },
  input: {
    padding: '16px 20px',
    border: '2px solid #e5e7eb',
    borderRadius: '12px',
    fontSize: '15px',
    outline: 'none'
  },
  select: {
    padding: '14px 16px',
    border: '2px solid #e5e7eb',
    borderRadius: '12px',
    fontSize: '15px',
    outline: 'none'
  },
  formActions: {
    display: 'flex',
    gap: '16px',
    justifyContent: 'flex-end'
  },
  cancelBtn: {
    padding: '10px 20px',
    background: '#f8fafc',
    color: '#64748b',
    border: '2px solid #e5e7eb',
    borderRadius: '12px',
    cursor: 'pointer',
    fontWeight: '500'
  },
  submitBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '14px 28px',
    background: 'linear-gradient(135deg, #10b981, #059669)',
    color: '#fff',
    border: 'none',
    borderRadius: '12px',
    fontWeight: '600',
    cursor: 'pointer',
    fontSize: '15px'
  },
  submitBtnDisabled: {
    opacity: 0.7,
    cursor: 'not-allowed'
  },
  searchContainer: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    maxWidth: '300px'
  },
  searchIcon: {
    position: 'absolute',
    left: '16px',
    color: '#9ca3af',
    pointerEvents: 'none'
  },
  searchInput: {
    width: '100%',
    padding: '12px 16px 12px 48px',
    border: '2px solid #e5e7eb',
    borderRadius: '12px',
    fontSize: '15px',
    outline: 'none'
  },
  tableContainer: {
    padding: '0'
  },
  tableWrapper: {
    overflowX: 'auto',
    borderRadius: '16px',
    border: '1px solid #e5e7eb'
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse'
  },
  tableHeadRow: {
    background: '#f8fafc'
  },
  tableHead: {
    padding: '20px 24px',
    textAlign: 'left',
    fontSize: '14px',
    fontWeight: '700',
    color: '#374151',
    textTransform: 'uppercase',
    letterSpacing: '0.05em'
  },
  tableRow: {
    borderBottom: '1px solid #f1f5f9',
    transition: 'all 0.2s ease'
  },
  tableCell: {
    padding: '20px 24px'
  },
  cellContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px'
  },
  batchName: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#1e293b'
  },
  academicYearChip: {
    padding: '8px 16px',
    background: 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)',
    color: '#1e40af',
    borderRadius: '20px',
    fontSize: '14px',
    fontWeight: '500',
    maxWidth: '200px'
  },
  batchId: {
    background: '#f3f4f6',
    color: '#6b7280',
    padding: '6px 12px',
    borderRadius: '8px',
    fontSize: '13px',
    fontFamily: 'monospace'
  },
  actionButtons: {
    display: 'flex',
    gap: '8px'
  },
  editBtn: {
    padding: '10px',
    borderRadius: '10px',
    background: 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)',
    border: 'none',
    cursor: 'pointer',
    color: '#1e40af',
    transition: 'all 0.2s ease'
  },
  deleteBtn: {
    padding: '10px',
    borderRadius: '10px',
    background: 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)',
    border: 'none',
    cursor: 'pointer',
    color: '#dc2626',
    transition: 'all 0.2s ease'
  },
  emptyState: {
    textAlign: 'center',
    padding: '80px 40px',
    color: '#6b7280'
  },
  emptyTitle: {
    fontSize: '24px',
    fontWeight: '700',
    color: '#374151',
    margin: '16px 0 8px 0'
  },
  emptySubtitle: {
    fontSize: '16px',
    margin: 0
  },
  spinner: {
    width: 18,
    height: 18,
    border: '2px solid transparent',
    borderTop: '2px solid white',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite'
  }
};

// Add CSS animation
const style = document.createElement('style');
style.textContent = `
  @keyframes spin { to { transform: rotate(360deg); } }
  tr:hover { background: #f8fafc !important; }
`;
document.head.appendChild(style);

export default BatchManagement;